import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAllAccounts,
  createAccount,
  updateAccount,
  deleteAccount,
  subscribeToAllAccounts,
  getPermissionLinks,
  createPermissionLink,
  deletePermissionLink,
  generateFeatureUrl
} from '../services/accounts';
import './AccountManagement.css';

function AccountManagement() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [showLinksModal, setShowLinksModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [permissionLinks, setPermissionLinks] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    role: 'user',
    permissions: {
      dataManagement: false,
      backup: false,
      query: false,
      dashboard: false,
      dataScreen: false,
      deviceManagement: false,
      timerManagement: false
    }
  });

  useEffect(() => {
    loadAccounts();

    // Subscribe to real-time updates
    const unsubscribe = subscribeToAllAccounts((accountsData) => {
      setAccounts(accountsData);
    });

    return () => unsubscribe();
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const accountsData = await getAllAccounts();
      setAccounts(accountsData);
    } catch (err) {
      console.error('Error loading accounts:', err);
      alert('載入失敗：' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();

    if (!formData.username.trim()) {
      alert('請輸入帳號名稱');
      return;
    }

    if (!formData.email.trim()) {
      alert('請輸入電子郵件');
      return;
    }

    try {
      await createAccount(formData);
      alert('帳號已創建');
      resetForm();
      setShowCreateModal(false);
    } catch (err) {
      console.error('Error creating account:', err);
      alert('創建失敗：' + err.message);
    }
  };

  const handleUpdateAccount = async (e) => {
    e.preventDefault();

    if (!editingAccount) return;

    try {
      await updateAccount(editingAccount.id, formData);
      alert('帳號已更新');
      resetForm();
      setEditingAccount(null);
    } catch (err) {
      console.error('Error updating account:', err);
      alert('更新失敗：' + err.message);
    }
  };

  const handleDeleteAccount = async (accountId, username) => {
    if (!window.confirm(`確定要刪除帳號「${username}」嗎？`)) return;

    try {
      await deleteAccount(accountId);
      alert('帳號已刪除');
    } catch (err) {
      console.error('Error deleting account:', err);
      alert('刪除失敗：' + err.message);
    }
  };

  const handleEditAccount = (account) => {
    setEditingAccount(account);
    setFormData({
      username: account.username,
      email: account.email,
      role: account.role,
      permissions: account.permissions || {
        dataManagement: false,
        backup: false,
        query: false,
        dashboard: false,
        dataScreen: false,
        deviceManagement: false,
        timerManagement: false
      }
    });
  };

  const handleTogglePermission = (permission) => {
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        [permission]: !formData.permissions[permission]
      }
    });
  };

  const handleToggleActive = async (accountId, currentStatus) => {
    try {
      await updateAccount(accountId, { isActive: !currentStatus });
    } catch (err) {
      console.error('Error toggling account status:', err);
      alert('更新失敗：' + err.message);
    }
  };

  const handleShowLinks = async (account) => {
    setSelectedAccount(account);
    setShowLinksModal(true);

    try {
      const links = await getPermissionLinks(account.id);
      setPermissionLinks(links);
    } catch (err) {
      console.error('Error loading permission links:', err);
      alert('載入連結失敗：' + err.message);
    }
  };

  const handleGenerateLinks = async () => {
    if (!selectedAccount) return;

    try {
      const baseUrl = window.location.origin;
      const features = [];

      // Generate links based on permissions
      if (selectedAccount.permissions.query) features.push('query');
      if (selectedAccount.permissions.dashboard) features.push('dashboard');
      if (selectedAccount.permissions.dataScreen) features.push('dataScreen');
      if (selectedAccount.permissions.deviceManagement) features.push('devices');
      if (selectedAccount.permissions.timerManagement) features.push('timers');
      if (selectedAccount.permissions.backup) features.push('backup');

      // Delete old links
      for (const link of permissionLinks) {
        await deletePermissionLink(link.id);
      }

      // Create new links
      const newLinks = [];
      for (const feature of features) {
        const url = generateFeatureUrl(baseUrl, feature, selectedAccount.id);
        const link = await createPermissionLink({
          accountId: selectedAccount.id,
          feature,
          url
        });
        newLinks.push(link);
      }

      setPermissionLinks(newLinks);
      alert('連結已生成');
    } catch (err) {
      console.error('Error generating links:', err);
      alert('生成連結失敗：' + err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      role: 'user',
      permissions: {
        dataManagement: false,
        backup: false,
        query: false,
        dashboard: false,
        dataScreen: false,
        deviceManagement: false,
        timerManagement: false
      }
    });
  };

  const handleCancelEdit = () => {
    setEditingAccount(null);
    resetForm();
  };

  const getFeatureName = (feature) => {
    const names = {
      query: '數據查詢',
      dashboard: '動態儀表板',
      dataScreen: '數據大屏',
      devices: '設備管理',
      timers: '計時器管理',
      backup: '資料備份'
    };
    return names[feature] || feature;
  };

  const formatLastLogin = (lastLoginAt) => {
    if (!lastLoginAt) return '從未登入';

    const timestamp = lastLoginAt.toDate ? lastLoginAt.toDate() : new Date(lastLoginAt);
    const now = new Date();
    const diffMs = now - timestamp;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return '剛剛';
    if (diffMins < 60) return `${diffMins} 分鐘前`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} 小時前`;
    return timestamp.toLocaleDateString('zh-TW');
  };

  if (loading) {
    return (
      <div className="account-management">
        <div className="loading">載入中...</div>
      </div>
    );
  }

  return (
    <div className="account-management">
      <header className="page-header">
        <div className="header-left">
          <h1>帳號管理</h1>
          <p>管理使用者帳號與權限設定</p>
        </div>
        <div className="header-right">
          <button
            className="btn btn-primary"
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
          >
            ➕ 新增帳號
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/dashboard')}
          >
            返回儀表板
          </button>
        </div>
      </header>

      <div className="accounts-grid">
        {accounts.length === 0 ? (
          <div className="empty-state">
            <p>尚無帳號</p>
            <button
              className="btn btn-primary"
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
            >
              創建第一個帳號
            </button>
          </div>
        ) : (
          accounts.map((account) => (
            <div key={account.id} className="account-card">
              <div className="account-header">
                <div>
                  <h3>{account.username}</h3>
                  <div className="account-email">{account.email}</div>
                </div>
                <div className="account-badges">
                  <span className={`role-badge role-${account.role}`}>
                    {account.role === 'admin' ? '管理員' : account.role === 'user' ? '使用者' : '訪客'}
                  </span>
                  <span className={`status-badge ${account.isActive ? 'active' : 'inactive'}`}>
                    {account.isActive ? '啟用' : '停用'}
                  </span>
                </div>
              </div>

              <div className="account-info">
                <div className="info-row">
                  <span className="label">最後登入：</span>
                  <span className="value">{formatLastLogin(account.lastLoginAt)}</span>
                </div>
                <div className="info-row">
                  <span className="label">權限：</span>
                  <div className="permissions-list">
                    {account.permissions?.query && <span className="permission-tag">查詢</span>}
                    {account.permissions?.dashboard && <span className="permission-tag">儀表板</span>}
                    {account.permissions?.dataScreen && <span className="permission-tag">大屏</span>}
                    {account.permissions?.deviceManagement && <span className="permission-tag">設備</span>}
                    {account.permissions?.timerManagement && <span className="permission-tag">計時器</span>}
                    {account.permissions?.backup && <span className="permission-tag">備份</span>}
                    {!Object.values(account.permissions || {}).some(v => v) && (
                      <span className="permission-tag empty">無權限</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="account-actions">
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => handleEditAccount(account)}
                >
                  ✏️ 編輯
                </button>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => handleShowLinks(account)}
                >
                  🔗 查看連結
                </button>
                <button
                  className={`btn btn-sm ${account.isActive ? 'btn-warning' : 'btn-success'}`}
                  onClick={() => handleToggleActive(account.id, account.isActive)}
                >
                  {account.isActive ? '停用' : '啟用'}
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => handleDeleteAccount(account.id, account.username)}
                >
                  🗑️ 刪除
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content account-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>新增帳號</h2>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateAccount} className="account-form">
              <div className="form-group">
                <label>帳號名稱 *</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="例如: user001"
                  required
                />
              </div>

              <div className="form-group">
                <label>電子郵件 *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="user@example.com"
                  required
                />
              </div>

              <div className="form-group">
                <label>角色</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  <option value="user">使用者</option>
                  <option value="admin">管理員</option>
                  <option value="viewer">訪客</option>
                </select>
              </div>

              <div className="form-group">
                <label>功能權限</label>
                <div className="permissions-checkboxes">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.permissions.query}
                      onChange={() => handleTogglePermission('query')}
                    />
                    <span>數據查詢</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.permissions.dashboard}
                      onChange={() => handleTogglePermission('dashboard')}
                    />
                    <span>動態儀表板</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.permissions.dataScreen}
                      onChange={() => handleTogglePermission('dataScreen')}
                    />
                    <span>數據大屏</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.permissions.deviceManagement}
                      onChange={() => handleTogglePermission('deviceManagement')}
                    />
                    <span>設備管理</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.permissions.timerManagement}
                      onChange={() => handleTogglePermission('timerManagement')}
                    />
                    <span>計時器管理</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.permissions.backup}
                      onChange={() => handleTogglePermission('backup')}
                    />
                    <span>資料備份</span>
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  取消
                </button>
                <button type="submit" className="btn btn-primary">
                  創建
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingAccount && (
        <div className="modal-overlay" onClick={handleCancelEdit}>
          <div className="modal-content account-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>編輯帳號</h2>
              <button className="close-btn" onClick={handleCancelEdit}>
                ✕
              </button>
            </div>

            <form onSubmit={handleUpdateAccount} className="account-form">
              <div className="form-group">
                <label>帳號名稱 *</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>電子郵件 *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>角色</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  <option value="user">使用者</option>
                  <option value="admin">管理員</option>
                  <option value="viewer">訪客</option>
                </select>
              </div>

              <div className="form-group">
                <label>功能權限</label>
                <div className="permissions-checkboxes">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.permissions.query}
                      onChange={() => handleTogglePermission('query')}
                    />
                    <span>數據查詢</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.permissions.dashboard}
                      onChange={() => handleTogglePermission('dashboard')}
                    />
                    <span>動態儀表板</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.permissions.dataScreen}
                      onChange={() => handleTogglePermission('dataScreen')}
                    />
                    <span>數據大屏</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.permissions.deviceManagement}
                      onChange={() => handleTogglePermission('deviceManagement')}
                    />
                    <span>設備管理</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.permissions.timerManagement}
                      onChange={() => handleTogglePermission('timerManagement')}
                    />
                    <span>計時器管理</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.permissions.backup}
                      onChange={() => handleTogglePermission('backup')}
                    />
                    <span>資料備份</span>
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCancelEdit}>
                  取消
                </button>
                <button type="submit" className="btn btn-primary">
                  更新
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Links Modal */}
      {showLinksModal && selectedAccount && (
        <div className="modal-overlay" onClick={() => setShowLinksModal(false)}>
          <div className="modal-content links-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>功能連結 - {selectedAccount.username}</h2>
              <button className="close-btn" onClick={() => setShowLinksModal(false)}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="links-actions">
                <button className="btn btn-primary" onClick={handleGenerateLinks}>
                  🔄 重新生成連結
                </button>
              </div>

              {permissionLinks.length === 0 ? (
                <div className="empty-links">
                  <p>尚無功能連結</p>
                  <p className="hint">點擊「重新生成連結」以根據權限創建連結</p>
                </div>
              ) : (
                <div className="links-list">
                  {permissionLinks.map((link) => (
                    <div key={link.id} className="link-item">
                      <div className="link-feature">{getFeatureName(link.feature)}</div>
                      <div className="link-url">
                        <input
                          type="text"
                          value={link.url}
                          readOnly
                          onClick={(e) => e.target.select()}
                        />
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => {
                            navigator.clipboard.writeText(link.url);
                            alert('連結已複製到剪貼簿');
                          }}
                        >
                          📋 複製
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AccountManagement;
